import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMDialog } from "../../../utils/PMDialog";
import PMSwitch from "../../../utils/PMSwitch";
import { PMCustomSelector } from "../../../utils/PMCustomSelector";
import { fetchChecklistTypes } from "../../../../api/quality/checklistType";

export const CreateReportsDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedSubsCount, 
  agreement 
}) => {
  const { t } = useTranslation();
  

  const handleSubmit = (values, { setSubmitting }) => {
    onSubmit(values);
    setSubmitting(false);
  };

  

  return (
    <PMDialog
      title={`Create Reports for ${selectedSubsCount} Selected Subs`}
      open={open}
      onClose={onClose}
      size="lg"
    >
      <Formik
        initialValues={{
          invoice_to_project_customer: false,
          create_single_report: false,
          checklist_type_id: null,
          description: "",
        }}
        validationSchema={Yup.object({
          description: Yup.string(),
        })}
        onSubmit={handleSubmit}
      >
        {(formik) => (
          <Form>
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Report Creation Settings
                    </h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>
                        This will create {formik.values.create_single_report ? "1" : selectedSubsCount} report(s) 
                        for the selected subs. Each report will be created with the settings below.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-6">
                  <PMSwitch
                    name="invoice_to_project_customer"
                    label="Invoice to Project Customer"
                    {...formik}
                  />
                </div>

                <div className="col-span-6 sm:col-span-6">
                  <PMSwitch
                    name="create_single_report"
                    label="Create Single Report (with checklist items for each sub)"
                    {...formik}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    When enabled, creates one report with checklist items for each selected sub.
                    When disabled, creates separate reports for each sub.
                  </p>
                </div>

                {!formik.values.create_single_report && (
                  <div className="col-span-6 sm:col-span-6">
                    <PMCustomSelector
                      name="checklist_type_id"
                      label="Checklist Type"
                      field="displayTitle"
                      fetchFunction={() => fetchChecklistTypes()}
                      {...formik}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Select a checklist type to automatically create checklists for the reports.
                    </p>
                  </div>
                )}

                <div className="col-span-6 sm:col-span-6">
                  <div className="flex justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Description Template
                    </label>
                  </div>
                  <textarea
                    name="description"
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Optional description template for the reports..."
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This description will be used for all created reports.
                  </p>
                </div>
              </div>

              {formik.values.create_single_report && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        Single Report Mode
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                        When creating a single report, checklist items will be created for each selected sub
                        with the format: &quot;{agreement?.type?.report_type?.title || 'Report Type'}: [Sub Name] - [Address]&quot;
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {formik.isSubmitting ? "Creating..." : `Create ${formik.values.create_single_report ? "1" : selectedSubsCount} Report(s)`}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
